import {Box, Button, Dialog, DialogActions, DialogContent, Grid, useTheme,} from "@mui/material";
import SectionHeader from "../../../../util/component/SectionHeader";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useRecoilState} from "recoil";
import {defaultRentalPeriodFilters, rentalPeriodFiltersAtom, unitsEnumAtom,} from "../../state/rentalPeriodsRecoil";
import {useTranslation} from "react-i18next";
import {DialogInterface, FormDialog, SelectElementModelFE,} from "../../api/types/generic";
import {useRentalPeriodRepository} from "../../api/repositories/RentalPeriodRepository";
import Loading from "../../common/components/Loading";
import {AgGridReact} from "ag-grid-react";
import {dateFormatter, useActionColDef} from "../../utils";
import {DEFAULT_COL_DEF, maxNumberYupLimit, SET_SNACKBAR,} from "../../constants";
import DialogComponent from "../../common/components/DialogComponent";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import Chip from "@mui/material/Chip";
import {FormProvider, useForm} from "react-hook-form";
import FormRentalPeriod from "./FormRentalPeriod/FormRentalPeriod";
import {
    FormRentalPeriodModel,
    FormSearchRentalPeriodModel,
    RentalPeriodEntityRequest,
    RentalPeriodEntityResponse,
} from "../../api/types/rentalPeriods";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import BasicToolbar from "../../common/components/BasicToolbar";
import ActionsPopover from "../../common/components/ActionsPopover";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import RentalPeriodFilters from "./Filters/RentalPeriodFilters";
import dayjs from "dayjs";
import {useConstantsRepository} from "../../api/repositories/ConstantsRepository";
import i18n from "i18next";

const RentalPeriods = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [rentalPeriodFilters, setRentalPeriodFilters] = useRecoilState(
        rentalPeriodFiltersAtom
    );
    const [, setUnitsEnum] = useRecoilState<string[]>(unitsEnumAtom);

    // Use Repository Hooks
    const {getTpUnitsEnum} = useConstantsRepository();
    const {
        getRentalPeriodsPaged,
        postRentalPeriod,
        putRentalPeriod,
        deleteRentalPeriod,
    } = useRentalPeriodRepository();

    const daysOfTheWeekUppercase = [
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: 7, label: dayjs().day(0).format("dddd").toUpperCase()},
    ];

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [initialValues] = useState<FormRentalPeriodModel>({
        name: "",
        tpUnit: "",
        description: "",
        rentalPeriodFrom: "",
        rentalPeriodTo: "",
        settingDateFrom: true,
        firstRound: true,
        quickPeriod: "",
        active: true,
        weekend: false,
        calendarFrom: "",
        calendarTo: "",
    });

    const handleGetStaticContent = async () => {
        try {
            const tpUnitsData = await getTpUnitsEnum();
            setUnitsEnum(tpUnitsData);
        } catch (e) {
        }
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    /* AG GRID - START */
    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            RentalPeriodEntityResponse,
            FormSearchRentalPeriodModel
        >({
            gridRef,
            api: getRentalPeriodsPaged,
            initialSortColId: "name",
            entityFilters: rentalPeriodFilters,
            customSort: "RentalPeriodEntityResponse",
        });

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            name: rentalPeriodFilters.name,
            tpUnit: (rentalPeriodFilters.tpUnit as string) || "",
            showActive: rentalPeriodFilters.showActive,
        });
    };

    useEffect(() => {
        setTimeout(() => {
            handleTriggerFiltersInitial();
        }, 500);
    }, [i18n.resolvedLanguage]);

    const handleTriggerResetFilters = () => {
        setRentalPeriodFilters(defaultRentalPeriodFilters);
        handleTriggerFilters({
            name: defaultRentalPeriodFilters.name,
            tpUnit: defaultRentalPeriodFilters.tpUnit as string || "",
            showActive: defaultRentalPeriodFilters.showActive
        });
    };

    useEffect(() => {
        if (gridRef?.current)
            handleTriggerFilters({
                name: rentalPeriodFilters.name,
                tpUnit: (rentalPeriodFilters.tpUnit as string) || "",
                showActive: rentalPeriodFilters.showActive,
            });
    }, [rentalPeriodFilters.showActive]);

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "name",
            headerName: t("pricing:rentalPeriods.form.labels.name"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.name"),
            flex: 1,
            filter: "text",
        },
        {
            field: "tpUnit",
            headerName: t("pricing:rentalPeriods.form.labels.unit"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.unit"),
            flex: 1,
            cellRenderer: (params: ICellRendererParams) => params.data.tpUnit,
            filter: "text",
        },
        {
            field: "description",
            headerName: t("pricing:rentalPeriods.form.labels.description"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.description"),
            flex: 1,
        },
        {
            field: "from",
            headerName: t("pricing:rentalPeriods.form.labels.from"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.from"),
            cellRenderer: (params: ICellRendererParams) =>
                params.data.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === params.data.from)
                        ?.label
                    : params.data.tpUnit === "ABSOLUTE"
                        ? dateFormatter(params.data.calendarFrom)
                        : params.data.from,
            flex: 1,
        },
        {
            field: "to",
            headerName: t("pricing:rentalPeriods.form.labels.to"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.to"),
            cellRenderer: (params: ICellRendererParams) =>
                params.data.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === params.data.to)?.label
                    : params.data.tpUnit === "ABSOLUTE"
                        ? dateFormatter(params.data.calendarTo)
                        : params.data.to,
            flex: 1,
        },
        {
            field: "showActive",
            headerName: t("pricing:rentalPeriods.form.labels.active"),
            headerTooltip: t("pricing:rentalPeriods.form.labels.active"),
            flex: 1,
            cellRenderer: (params: any) => {
                return params.data.active ? (
                    <Chip
                        label={t("pricing:labels.yes")}
                        sx={{bgcolor: "#BEE5CA"}}
                        variant={"filled"}
                    />
                ) : (
                    <Chip
                        label={t("pricing:labels.no")}
                        sx={{bgcolor: "#F39D9D"}}
                        variant={"filled"}
                    />
                );
            },
            filter: "text",
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_DETAIL_RENTAL_PERIOD";
                            });
                        }),
                    }}
                    update={{
                        action: updateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_RENTAL_PERIOD";
                            });
                        }),
                    }}
                    del={{
                        action: deleteButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_RENTAL_PERIOD";
                            });
                        }),
                    }}
                />
            ),
        },
    ];

    const defaultColDef = DEFAULT_COL_DEF;
    /* AG GRID - END */

    /* FORM - START */
    const validationSchema = yup
        .object()
        .shape({
            name: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .min(2),
            tpUnit: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .nullable(),
            description: yup.string(),
            weekend: yup.boolean().nullable(),
            rentalPeriodFrom: yup.mixed().when(["tpUnit", "weekend"], {
                is: (tpUnit: string) => tpUnit === "ABSOLUTE",
                then: yup.mixed().nullable(true).notRequired(),
                otherwise: yup.mixed().when("weekend", {
                    is: true,
                    then: yup
                        .object()
                        .shape({
                            code: yup.number(),
                            label: yup.string(),
                        })
                        .nullable()
                        .required(t("translation:validation.fieldMandatory")),
                    otherwise: yup
                        .number()
                        .integer(t("pricing:alert.errors.integer"))
                        .typeError(t("pricing:alert.errors.integer"))
                        .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                        .required(t("translation:validation.fieldMandatory"))
                        .max(
                            maxNumberYupLimit,
                            `${t("pricing:validation.numberLowerThan")} ${maxNumberYupLimit}`
                        ),
                }),
            }),
            rentalPeriodTo: yup.mixed().when(["tpUnit", "weekend"], {
                is: (tpUnit: string) => tpUnit === "ABSOLUTE",
                then: yup.mixed().nullable(true).notRequired(),
                otherwise: yup.mixed().when("weekend", {
                    is: true,
                    then: yup
                        .object()
                        .shape({
                            code: yup.number(),
                            label: yup.string(),
                        })
                        .nullable()
                        .required(t("translation:validation.fieldMandatory")),
                    otherwise: yup
                        .number()
                        .integer(t("pricing:alert.errors.integer"))
                        .typeError(t("pricing:alert.errors.integer"))
                        .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                        .required(t("translation:validation.fieldMandatory"))
                        .min(
                            yup.ref("rentalPeriodFrom"),
                            ({min}) => t("pricing:validation.numberGreaterThan") + min
                        )
                        .max(
                            maxNumberYupLimit,
                            `${t("pricing:validation.numberLowerThan")} ${maxNumberYupLimit}`
                        ),
                }),
            }),
            calendarFrom: yup
                .string()
                .nullable()
                .when("tpUnit", {
                    is: "ABSOLUTE",
                    then: yup
                        .string()
                        .nullable()
                        .required(t("translation:validation.fieldMandatory")),
                }),
            calendarTo: yup
                .string()
                .nullable()
                .when("tpUnit", {
                    is: "ABSOLUTE",
                    then: yup
                        .string()
                        .nullable()
                        .required(t("translation:validation.fieldMandatory")),
                }),
        })
        .defined();

    // Use Form
    const form = useForm<FormRentalPeriodModel>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: false,
    });
    /* FORM - END */

    /* TABLE ACTIONS - START */
    const handleClickViewRentalPeriod = (
        rentalPeriod: RentalPeriodEntityResponse
    ) => {
        if (rentalPeriod) {
            const result = {
                id: rentalPeriod.id,
                name: rentalPeriod.name,
                tpUnit: rentalPeriod.tpUnit,
                description: rentalPeriod.description,
                rentalPeriodFrom: rentalPeriod.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === rentalPeriod.from)
                    : rentalPeriod.from,
                rentalPeriodTo: rentalPeriod.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === rentalPeriod.to)
                    : rentalPeriod.to,
                settingDateFrom: true,
                firstRound: true,
                quickPeriod: "",
                active: Boolean(rentalPeriod.active),
                weekend: Boolean(rentalPeriod.weekend),
                calendarFrom: rentalPeriod.calendarFrom,
                calendarTo: rentalPeriod.calendarTo,
            };
            form.reset(result);
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: true,
            });
        }
    };

    const handleClickPutRentalPeriod = (
        rentalPeriod: RentalPeriodEntityResponse
    ) => {
        if (rentalPeriod) {
            const result = {
                id: rentalPeriod.id,
                name: rentalPeriod.name,
                tpUnit: rentalPeriod.tpUnit,
                description: rentalPeriod.description,
                rentalPeriodFrom: rentalPeriod.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === rentalPeriod.from)
                    : rentalPeriod.from,
                rentalPeriodTo: rentalPeriod.weekend
                    ? daysOfTheWeekUppercase.find((d) => d.code === rentalPeriod.to)
                    : rentalPeriod.to,
                settingDateFrom: true,
                firstRound: true,
                quickPeriod: "",
                active: Boolean(rentalPeriod.active),
                weekend: Boolean(rentalPeriod.weekend),
                calendarFrom: rentalPeriod.calendarFrom,
                calendarTo: rentalPeriod.calendarTo,
            };
            form.reset(result);
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: false,
            });
        }
    };

    const handleConfirmSave = async () => {
        const values = form.getValues();
        const newRentalPeriod: RentalPeriodEntityRequest = {
            name: values.name,
            tpUnit: values.tpUnit as string,
            description: values.description,
            from: values.weekend
                ? ((values.rentalPeriodFrom as SelectElementModelFE).code as number)
                : (values.rentalPeriodFrom as number),
            to: values.weekend
                ? ((values.rentalPeriodTo as SelectElementModelFE).code as number)
                : (values.rentalPeriodTo as number),
            active: values.active,
            weekend: values.weekend as boolean,
            calendarFrom: values.calendarFrom
                ? dayjs(values.calendarFrom).toISOString()
                : null,
            calendarTo: values.calendarTo
                ? dayjs(values.calendarTo).toISOString()
                : null,
        };

        try {
            values?.id
                ? await putRentalPeriod({
                    id: values?.id,
                    ...JSON.parse(JSON.stringify(newRentalPeriod)),
                })
                : await postRentalPeriod(newRentalPeriod);

            // reset form
            setFormDialog(null);
            form.reset(initialValues);
            // get lista rental period
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const handleConfirmDelete = async (deletingRowIndex: number) => {
        try {
            await deleteRentalPeriod(Number(deletingRowIndex));
            setDialog(null);
            form.reset(initialValues);
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const viewButtonClicked = (data: RentalPeriodEntityResponse) => {
        handleClickViewRentalPeriod(data);
    };

    const updateButtonClicked = (data: RentalPeriodEntityResponse) => {
        handleClickPutRentalPeriod(data);
    };

    const deleteButtonClicked = (data: RentalPeriodEntityResponse) => {
        setDialog({
            open: true,
            message: t("pricing:rentalPeriods.dialogContents.removeRentalPeriodInfo"),
            onClickConfirm: () => handleConfirmDelete(data.id),
        });
    };
    /* TABLE ACTIONS - END */

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_RENTAL_PERIOD";
                        });
                    })
                        ? {
                            onClick: () =>
                                setFormDialog({
                                    open: true,
                                    onClickConfirm: form.handleSubmit(handleConfirmSave),
                                    readonly: false,
                                }),
                        }
                        : undefined
                }
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) =>
                        setRentalPeriodFilters({
                            ...rentalPeriodFilters,
                            showActive: checked,
                        }),
                    value: rentalPeriodFilters.showActive as boolean,
                }}
            />
        ),
    };

    return (
        <Box>
            <SectionHeader
                title={t("translation:menu.pricing.pricing_rental_period")}
            />
            {formDialog?.open && (
                <Dialog open={formDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={() => {
                            form.reset(initialValues);
                            setFormDialog(null);
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:rentalPeriods.section.addRentalPeriod")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        <FormProvider {...form}>
                            <FormRentalPeriod readonly={formDialog.readonly}/>
                        </FormProvider>
                    </DialogContent>
                    {!formDialog.readonly && (
                        <DialogActions
                            style={{
                                backgroundColor: theme.palette.neutral.bright,
                                padding: "1rem",
                            }}
                        >
                            <Button
                                variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    form.reset(initialValues);
                                    setFormDialog(null);
                                }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button
                                sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={formDialog?.onClickConfirm}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            )}

            <RentalPeriodFilters
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                </Grid>
            </Grid>
            <Loading/>

            {dialog?.open && (
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                        form.reset(initialValues);
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />
            )}
        </Box>
    );
};

export default RentalPeriods;
